import React, { useState, useEffect } from "react";
import useSWR, { mutate } from "swr";
import AutoCompleteSelect from "./AutoCompleteSelect";
import {
  faCheckCircle,
  faClose,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MessageModal from "./MessageModal";

const fetcher = (url) =>
  fetch(url, { credentials: "include" }).then((res) => res.json());

const AddCTScanRequestRegister = () => {
  const [loading, setLoading] = useState(false);

  // Patient fields
  const [patientFullname, setPatientFullname] = useState("");
  const [patientGender, setPatientGender] = useState("");
  const [patientDOB, setPatientDOB] = useState("");
  const [patientPhone, setPatientPhone] = useState("");
  const [patientEmail, setPatientEmail] = useState("");

  // CT Scan fields
  const [ctscanInvestigation, setCTScanInvestigation] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [selectedInvestigationLabel, setSelectedInvestigationLabel] =
    useState("");
  const [selectedInvestigationNID, setSelectedInvestigationNID] =
    useState(null);
  const [ctscanClinicalDiag, setCTScanClinicalDiag] = useState("");
  const [ctscanComments, setCTScanComments] = useState("");

  const [csrfToken, setCsrfToken] = useState("");
  const [tokenReady, setTokenReady] = useState(false);
  const [isMessageModalVisible, setIsMessageModalVisible] = useState(false);

  // URLs
  const csrfTokenURL = process.env.REACT_APP_SESSION_TOKEN_URL;
  const investigationsAPI = process.env.REACT_APP_GET_INVESTIGATION_API;
  const addRequestURL = process.env.REACT_APP_CT_SCAN_ADD_REQUEST_URL;

  // Fetch CSRF token
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const res = await fetch(csrfTokenURL, { credentials: "include" });
        const token = await res.text();
        if (token) {
          setCsrfToken(token);
          setTokenReady(true);
        }
      } catch (err) {
        console.error("Error fetching CSRF token:", err);
        setTokenReady(false);
      }
    };
    fetchToken();
  }, [csrfTokenURL]);

  // Fetch investigations
  const { data: investigationsData } = useSWR(
    investigationsAPI || null,
    fetcher
  );

  // ✅ Include both uuid and nid for the API payload
  const formattedInvestigations =
    investigationsData?.map((inv) => ({
      uuid: inv.uuid,
      nid: inv.nid, // <-- Add nid
      label: `${inv.title} - GHC${parseFloat(
        inv.field_investigation_price
      ).toFixed(2)}`,
      title: inv.title,
    })) || [];

  // ✅ Select investigation (keep both uuid and nid)
  const handleSelectInvestigation = (option) => {
    if (option && option.uuid) {
      setCTScanInvestigation(option);
      setSelectedInvestigationLabel(option.label);
      setSelectedInvestigationNID(option.nid);

      // ✅ Log the selected investigation NID
      console.log("🧾 Selected Investigation NID:", option.nid);
    } else {
      setCTScanInvestigation(null);
      setSelectedInvestigationLabel("");
    }
  };

  const toggleModalClose = () => setIsMessageModalVisible(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!patientFullname) return alert("Please enter the patient full name.");
    if (!patientGender) return alert("Please select the patient gender.");
    if (!patientDOB) return alert("Please select the patient date of birth.");
    if (!patientPhone) return alert("Please enter the patient phone number.");
    if (!patientEmail) return alert("Please enter the patient email address.");
    if (!ctscanInvestigation) return alert("Please select an investigation.");
    if (!ctscanClinicalDiag)
      return alert("Please enter the clinical diagnosis.");
    if (!ctscanComments) return alert("Please enter comments.");

    if (!tokenReady || !csrfToken) {
      alert("Please wait — the form is still initializing.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        patientfullname: patientFullname,
        patientgender: patientGender,
        patientdob: patientDOB,
        patientphone: patientPhone,
        patientemail: patientEmail,
        investigation: selectedInvestigationNID, // send UUID to Drupal
        investigation_nid: ctscanInvestigation.nid,
        investigation_uuid: ctscanInvestigation.uuid,
        investigation_label: ctscanInvestigation.label,
        clinicaldiag: ctscanClinicalDiag,
        comments: ctscanComments,
      };

      console.log("📦 Payload sent to Drupal:", payload);

      const response = await fetch(addRequestURL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error("❌ Error submitting request:", result);
        alert(result.error || "Failed to submit CT Scan Request.");
        setLoading(false);
        return;
      }
      mutate(`${process.env.REACT_APP_CT_SCAN_REQUEST_URL}`);

      // ✅ Reset form
      setPatientFullname("");
      setPatientGender("");
      setPatientDOB("");
      setPatientPhone("");
      setPatientEmail("");
      setCTScanInvestigation(null);
      setSelectedInvestigationLabel("");
      setCTScanClinicalDiag("");
      setCTScanComments("");
      setIsMessageModalVisible(true);
    } catch (error) {
      console.error("⚠️ Submission error:", error);
      alert("Network or server error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-ctscan-request-register">
      <form onSubmit={handleSubmit} className="patient-form">
        <div className="form-group">
          <label>Full Name</label>
          <input
            type="text"
            value={patientFullname}
            onChange={(e) => setPatientFullname(e.target.value)}
            required
            disabled={!tokenReady}
          />
        </div>

        <div className="form-group">
          <label>Gender</label>
          <select
            value={patientGender}
            onChange={(e) => setPatientGender(e.target.value)}
            required
            disabled={!tokenReady}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>

        <div className="form-group">
          <label>Date of Birth</label>
          <input
            type="date"
            value={patientDOB}
            onChange={(e) => setPatientDOB(e.target.value)}
            required
            disabled={!tokenReady}
          />
        </div>

        <div className="form-group">
          <label>Phone Number</label>
          <input
            type="tel"
            value={patientPhone}
            onChange={(e) => setPatientPhone(e.target.value)}
            required
            disabled={!tokenReady}
          />
        </div>

        <div className="form-group">
          <label>Email Address</label>
          <input
            type="email"
            value={patientEmail}
            onChange={(e) => setPatientEmail(e.target.value)}
            required
            disabled={!tokenReady}
          />
        </div>

        <div className="form-group">
          <label>Investigation</label>
          <AutoCompleteSelect
            options={formattedInvestigations}
            labelKey="label"
            valueKey="uuid"
            placeholder="Search Investigation"
            onSelect={handleSelectInvestigation}
            disabled={!tokenReady}
          />
        </div>

        <div className="form-group">
          <label>Clinical Diagnosis</label>
          <textarea
            value={ctscanClinicalDiag}
            onChange={(e) => setCTScanClinicalDiag(e.target.value)}
            required
            disabled={!tokenReady}
          />
        </div>

        <div className="form-group">
          <label>Comments</label>
          <textarea
            value={ctscanComments}
            onChange={(e) => setCTScanComments(e.target.value)}
            required
            disabled={!tokenReady}
          />
        </div>

        <div className="submitGroup">
          <button type="submit" disabled={loading || !tokenReady}>
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : "Submit"}
          </button>
        </div>
      </form>

      {isMessageModalVisible && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          fontSize={20}
          message="CT Scan Request Added Successfully!"
          onClose={toggleModalClose}
          rightIcon={faClose}
        />
      )}
    </div>
  );
};

export default AddCTScanRequestRegister;
